import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { UserContext } from '../../component/common/UserContext';
import Chart from 'react-apexcharts';
import config from '../../envconfig';
import { useNavigate } from 'react-router-dom';


const LogisticWidget = () => {
    const PAGE_SIZE = 5;
    const [logisticsData, setLogisticsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartWidth, setChartWidth] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const { UM_GroupId, UM_CompanyGrpID, selectedScpId, UM_CompanyID, UM_UserId, UM_UserCode, UM_DefaultSCPId } = useContext(UserContext);
    const chartRef = useRef(null);
    const navigate = useNavigate();
    // console.log("jsfdjsbfjwrbujsfbjskfbjsfbjsfbjsfbjsfbjsfbjsfbjsfbjsfb");

    useEffect(() => {
        const fetchLogisticsData = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/logistics/${selectedScpId}`);
                setLogisticsData(response.data);
                setCurrentPage(1);
                console.log("LOGISTIC -----", response.data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchLogisticsData();
    }, [selectedScpId]);

    useEffect(() => {
        const updateChartWidth = () => {
            if (chartRef.current) {
                setChartWidth(chartRef.current.clientWidth || 0);
            }
        };

        updateChartWidth();

        const resizeObserver = new ResizeObserver(updateChartWidth);
        if (chartRef.current) {
            resizeObserver.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                resizeObserver.unobserve(chartRef.current);
            }
        };
    }, []);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    const wrapXAxisLabel = (label, maxCharsPerLine = 12) => {
        const words = String(label || '').split(' ');
        const lines = [];
        let currentLine = '';

        words.forEach((word) => {
            if (word.length > maxCharsPerLine) {
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = '';
                }
                for (let i = 0; i < word.length; i += maxCharsPerLine) {
                    lines.push(word.slice(i, i + maxCharsPerLine));
                }
                return;
            }

            const nextLine = currentLine ? `${currentLine} ${word}` : word;
            if (nextLine.length <= maxCharsPerLine) {
                currentLine = nextLine;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        });

        if (currentLine) lines.push(currentLine);
        return lines.length ? lines : [''];
    };

    const totalPages = Math.max(1, Math.ceil(logisticsData.length / PAGE_SIZE));
    const effectivePage = Math.min(currentPage, totalPages);
    const pageStart = (effectivePage - 1) * PAGE_SIZE;
    const paginatedLogisticsData = logisticsData.slice(pageStart, pageStart + PAGE_SIZE);
    const totalCount = logisticsData.length;
    const rangeStart = totalCount === 0 ? 0 : pageStart + 1;
    const rangeEnd = Math.min(pageStart + PAGE_SIZE, totalCount);

    const labelCharsPerLine = (() => {
        if (!paginatedLogisticsData.length) return 10;
        const safeWidth = chartWidth || 600;
        const pixelsPerCategory = safeWidth / paginatedLogisticsData.length;
        const estimatedChars = Math.floor(pixelsPerCategory / 12);
        return Math.max(4, Math.min(10, estimatedChars));
    })();

    const wrappedCategories = paginatedLogisticsData.map((company) =>
        wrapXAxisLabel(company.LGCM_Name || '', labelCharsPerLine)
    );

    const maxLabelLines = wrappedCategories.reduce(
        (maxLines, labelLines) => Math.max(maxLines, labelLines.length),
        1
    );
    const xAxisMinHeight = Math.max(48, maxLabelLines * 16);
    const xAxisMaxHeight = Math.max(120, maxLabelLines * 20);
    const yAxisMinHeight = 28;
    const paginationSpace = 24;
    const bottomPadding = Math.max(24, Math.min(56, maxLabelLines * 8)) + paginationSpace;

    const chartOptions = {
        chart: {
            type: 'bar',
            height: '100%',
            width: '100%',
            parentHeightOffset: 0,
            toolbar: {
                show: false,
                responsive: true,

                animations: {
                    enabled: true,
                },
            },
            events: {
                dataPointSelection: (event, chartContext, { dataPointIndex }) => {
                    const selectedCompany = paginatedLogisticsData[dataPointIndex];

                    console.log(`Bar clicked: Logistic Name - ${selectedCompany?.LGCM_Name || ''}`);
                    navigate("/lgcmlist")
                }
            }
        },
        title: {
            text: 'Logistic Data',
            align: 'left',  // Aligns the title to the left
            margin: 10,
            offsetX: 0,     // Adjust this value to move the title more to the left if needed
            offsetY: 1,
            floating: false,
            style: {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#2B2A29',
            },
            cssClass: 'chart-title-border'
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '50%',
                endingShape: 'rounded',
                borderRadius: 4,
            },
        },
        grid: {
            padding: {
                left: 12,
                bottom: bottomPadding,
            },
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            categories: wrappedCategories,
            labels: {
                rotate: 0,
                trim: false,
                hideOverlappingLabels: false,
                minHeight: xAxisMinHeight,
                maxHeight: xAxisMaxHeight,
                style: {
                    fontSize: '11px',
                },
            },
            title: {
                text: 'Logistics Company',
                offsetY: 2,
                style: {
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#000',
                    // fontFamily: "sans-serif"

                },
            },
        },
        yaxis: {
            title: {
                text: '',
            },
            labels: {
                minWidth: yAxisMinHeight,
                maxWidth: 36,
                style: {
                    fontSize: '11px',
                },
            },
        },
        tooltip: {
            y: {
                formatter: function (val, { series, seriesIndex, dataPointIndex, w }) {
                    const company = paginatedLogisticsData[dataPointIndex];
                    return `Active Vehicles : ${val} , In Transit Shipments : ${company?.ShipmentCount || 0}`;
                },
            },
        },
        fill: {
            opacity: 1,
        },
        colors: ['#4AB97D'],
    };

    const chartSeries = [
        {
            name: '',
            data: paginatedLogisticsData.map((company) => company.VehicleCount),
        },
    ];

    return (
        <div ref={chartRef} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexGrow: 1, width: '100%', height: '100%', position: 'relative' }}>
                <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height="100%"
                    width="100%"
                />
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: '4px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={effectivePage <= 1}
                        aria-label="Previous page"
                        title="Previous page"
                        style={{
                            cursor: effectivePage <= 1 ? 'not-allowed' : 'pointer',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            lineHeight: 1,
                            fontSize: '24px',
                            color: effectivePage <= 1 ? '#9CA3AF' : '#111827',
                        }}
                    >
                        {'<'}
                    </button>
                    <span style={{ sifontSize: '15px', lineHeight: 1, color: '#111827' }}>{`${rangeStart} - ${rangeEnd} / ${totalCount}`}</span>
                    <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={effectivePage >= totalPages}
                        aria-label="Next page"
                        title="Next page"
                        style={{
                            cursor: effectivePage >= totalPages ? 'not-allowed' : 'pointer',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            lineHeight: 1,
                            fontSize: '24px',
                            color: effectivePage >= totalPages ? '#9CA3AF' : '#111827',
                        }}
                    >
                        {'>'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LogisticWidget;
